import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { gurukulService } from '../../services';
import { formatCurrency } from '../../utils/formatters';
import { getProvinces, getDistricts, getAllDistricts, getMunicipalities } from '../../constants/nepalLocations';

const GurukulList = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [province, setProvince] = useState('');
  const [district, setDistrict] = useState('');
  const [localLevel, setLocalLevel] = useState('');
  const [availableDistricts, setAvailableDistricts] = useState(getAllDistricts());
  const [availableMunicipalities, setAvailableMunicipalities] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const LIMIT = 50;

  const provinces = getProvinces();

  // Filters change effect
  useEffect(() => {
    setOffset(0);
    fetchCandidates(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, province, district, localLevel]);

  // Update available districts when province changes
  useEffect(() => {
    if (province) {
      const provinceDistricts = getDistricts(province);
      setAvailableDistricts(provinceDistricts);
      if (district && !provinceDistricts.includes(district)) {
        setDistrict('');
      }
    } else {
      setAvailableDistricts(getAllDistricts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province]);

  // Update available municipalities when province or district changes
  useEffect(() => {
    if (province && district) {
      const munis = getMunicipalities(province, district);
      setAvailableMunicipalities(munis);
      if (localLevel && !munis.includes(localLevel)) {
        setLocalLevel('');
      }
    } else {
      setAvailableMunicipalities([]);
      if (localLevel) {
        setLocalLevel('');
      }
    }
  }, [province, district, localLevel]);

  useEffect(() => {
    setAvailableDistricts(getAllDistricts());
  }, []);

  const fetchCandidates = async (currentOffset = 0, replace = false) => {
    try {
      setLoading(true);
      const params = {
        limit: LIMIT,
        offset: currentOffset,
        sortBy: 'createdAt',
        order: 'DESC',
      };
      if (search) params.search = search;
      if (province) params.province = province;
      if (district) params.district = district;
      if (localLevel) params.nagarpalikaGaupalika = localLevel;

      const data = await gurukulService.getAllGurukuls(params);

      const items = data?.data?.items || [];
      const count = data?.data?.totalCount || 0;

      setTotalCount(count);

      // Fisher-Yates Shuffle for the current batch
      // Note: Shuffling per page might result in inconsistent global order if not handled by backend, 
      // but retaining existing frontend shuffle behavior for the fetched batch.
      const shuffledItems = [...items];
      for (let i = shuffledItems.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledItems[i], shuffledItems[j]] = [shuffledItems[j], shuffledItems[i]];
      }

      setCandidates(prev => replace ? shuffledItems : [...prev, ...shuffledItems]);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      if (replace) setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchCandidates(newOffset, false);
  };

  return (
    <div className="min-h-screen bg-primary-50">
      <PageHeader
        title={t('gurukul.pageTitle')}
        subtitle={t('gurukul.description') || "Explore and support Gurukuls across Nepal."}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Search and Filter Trigger */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder={t('gurukul.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-primary-200 rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent sm:text-sm transition-all duration-200 shadow-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary-200 rounded-2xl text-base font-semibold text-primary-700 bg-white hover:bg-primary-50 transition-all duration-200 shadow-sm relative group"
          >
            <svg className="w-5 h-5 mr-2 text-primary-600 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('gurukul.filters')}
            {(province || district || localLevel) && (
              <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white ring-4 ring-primary-50">
                {[province, district, localLevel].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Modal */}
        {
          showFilters && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
              <div
                className="absolute inset-0 bg-primary-900/40 backdrop-blur-sm transition-opacity"
                onClick={() => setShowFilters(false)}
              ></div>

              <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp">
                <div className="px-6 py-5 border-b border-primary-100 flex items-center justify-between bg-primary-50/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-xl text-primary-600">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{t('gurukul.advancedFilters')}</h3>
                  </div>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-2 hover:bg-primary-100 rounded-full text-gray-400 hover:text-primary-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-8 space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('gurukul.province')}</label>
                    <select
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                      className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                    >
                      <option value="">{t('gurukul.allProvinces')}</option>
                      {provinces.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('gurukul.district')}</label>
                    <select
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                    >
                      <option value="">{t('gurukul.allDistricts')}</option>
                      {availableDistricts.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('gurukul.nagarpalikaGaupalika')}</label>
                    <select
                      value={localLevel}
                      onChange={(e) => setLocalLevel(e.target.value)}
                      className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                    >
                      <option value="">
                        {t('gurukul.allNagarpalikaGaupalika') !== 'gurukul.allNagarpalikaGaupalika'
                          ? t('gurukul.allNagarpalikaGaupalika')
                          : 'सबै नगरपालिका / गाउँपालिका'}
                      </option>
                      {availableMunicipalities.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                </div>

                <div className="px-8 py-6 bg-primary-50/30 border-t border-primary-100 flex gap-4">
                  <button
                    onClick={() => {
                      setProvince('');
                      setDistrict('');
                      setLocalLevel('');
                      setSearch('');
                    }}
                    className="flex-1 px-6 py-3 border-2 border-primary-200 text-primary-700 rounded-2xl font-bold hover:bg-white transition-all"
                  >
                    {t('gurukul.clearAll')}
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-2xl font-bold hover:bg-primary-700 shadow-lg shadow-primary-600/20 transition-all"
                  >
                    {t('gurukul.applyFilters')}
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {/* Candidates Grid */}
        {
          candidates.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {candidates.map((candidate) => (
                  <div key={candidate.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                    <Link
                      to={`/gurukuls/${candidate.id}`}
                      className="relative h-80 bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden block"
                      aria-label={`${t('actions.viewProfile')} - ${candidate.name}`}
                    >
                      {candidate.photoKeyUrl && !imageErrors[candidate.id] ? (
                        <img
                          src={candidate.photoKeyUrl}
                          alt={candidate.name}
                          className="absolute inset-0 w-full h-full object-cover scale-110 group-hover:scale-105 transition-transform duration-500"
                          style={{ objectPosition: 'center 20%' }}
                          onError={() => {
                            setImageErrors(prev => ({ ...prev, [candidate.id]: true }));
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                          <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center">
                            <span className="text-5xl font-bold text-primary-600">
                              {candidate.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Subtle gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      {/* Official Badge */}
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 shadow-lg flex items-center gap-1.5 border border-primary-200">
                        <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs font-bold text-primary-700">Official</span>
                      </div>
                    </Link>
                    <div className="p-6">
                      <Link to={`/gurukuls/${candidate.id}`} className="block">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {candidate.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <svg className="w-4 h-4 text-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">{candidate.province && `${candidate.province}, `}{candidate.district}, {candidate.nagarpalikaGaupalika}</span>
                      </div>
                      <Link
                        to={`/gurukuls/${candidate.id}`}
                        className="block w-full text-center bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        {t('actions.viewProfile')}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {candidates.length < totalCount && (
                <div className="flex justify-center mt-12 mb-8">
                  {loading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  ) : (
                    <button
                      onClick={handleViewMore}
                      className="bg-white text-primary-600 border border-primary-600 px-6 py-2 rounded-full font-medium hover:bg-primary-50 transition-colors duration-200"
                    >
                      {t('actions.viewMore')}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('gurukul.noGurukuls')}</p>
            </div>
          )
        }
      </div >
    </div >
  );
};

export default GurukulList;
